import type { TFunction } from 'i18next'
import { Alert } from '@agion/ui'
import { diskSizeBounds } from './vmDeployment'

/**
 * Boot disk copy for the advanced-settings panel, one component per cloud
 * because the consequence of a size change differs in kind: UpCloud grows the
 * disk through a restart, Azure through a deallocation, and GCP not at all.
 *
 * Every single-VM module declares disk_size_gb with a default of 50, so an
 * empty field is a request for 50 GB. The panel resends the recorded size in
 * its place, which is why "leave it empty" is safe advice only while a size is
 * on record.
 */
interface DiskHintProps {
  t: TFunction<readonly ['pages']>
  diskSizeGb: string
  /** Re-apply: the panel resends the recorded size when the field is empty. */
  reapply?: boolean
  /** Size the last successful apply recorded, when known. */
  appliedDiskGb?: number
}

interface UpcloudDiskHintProps extends DiskHintProps {
  /** Storage the selected plan includes; undefined for a plan typed by hand. */
  includedStorageGb?: number
}

export function UpcloudDiskHints(props: UpcloudDiskHintProps) {
  const { t, diskSizeGb, appliedDiskGb } = props
  const grows = !!diskSizeGb && (appliedDiskGb === undefined || Number(diskSizeGb) > appliedDiskGb)
  const sentences = props.reapply
    ? [
        t('pages:onboarding.configure.diskKeepUpcloud', 'Leave empty to keep the current disk. A larger size grows the disk in place; UpCloud disks cannot shrink.'),
        appliedDiskGb !== undefined && t('pages:onboarding.configure.diskCurrentUpcloud', { defaultValue: 'Current disk: {{size}} GB.', size: appliedDiskGb }),
      ]
    : [
        props.includedStorageGb !== undefined && t('pages:onboarding.configure.diskIncludedUpcloud', { defaultValue: 'This plan includes {{size}} GB. Storage above that is billed separately.', size: props.includedStorageGb }),
        t('pages:onboarding.configure.diskGrowOnlyUpcloud', '25–2048 GB. You can grow the disk later, but UpCloud disks cannot shrink.'),
      ]
  return (
    <>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {sentences.filter(Boolean).join(' ')}
      </p>
      {props.reapply && grows && (
        <Alert variant="warning">
          {t(
            'pages:onboarding.configure.diskGrowRestartsUpcloud',
            'A larger disk restarts the server: UpCloud stops it, grows the disk and its file system, and starts it again. Expect a few minutes of downtime.',
          )}
        </Alert>
      )}
    </>
  )
}

/**
 * GCP boot disk size is ForceNew on google_compute_instance, and gcp-vm
 * creates the disk inside the instance rather than as its own resource. A new
 * size therefore destroys the VM and its disk and builds both from the image.
 * Provider 6.x has no in-place resize, so the panel says rebuild, not restart.
 */
export function GcpDiskHints(props: DiskHintProps) {
  const { t, diskSizeGb, appliedDiskGb } = props
  const bounds = diskSizeBounds('gcp')
  const rebuilds = !!diskSizeGb && Number(diskSizeGb) !== appliedDiskGb
  // An empty field only keeps the current disk while a recorded size exists to
  // resend. With nothing recorded the module default of 50 GB applies, so on a
  // larger machine the "leave it empty" advice is what destroys the disk.
  const sentences = props.reapply
    ? appliedDiskGb !== undefined
      ? [
          t('pages:onboarding.configure.diskKeepGcp', 'Leave empty to keep the current boot disk.'),
          t('pages:onboarding.configure.diskCurrentGcp', { defaultValue: 'Current boot disk: {{size}} GB.', size: appliedDiskGb }),
        ]
      : [
          t('pages:onboarding.configure.diskUnrecordedGcp', 'No current boot disk size is on record for this machine. An empty field asks for the module default of 50 GB, which destroys this virtual machine and its disk if it runs on anything larger. Enter the size it runs on today.'),
        ]
    : [
        t('pages:onboarding.configure.diskRangeGcp', { defaultValue: '{{min}}–{{max}} GB. The Agion image is built at {{min}} GB.', ...bounds }),
      ]
  return (
    <>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {sentences.filter(Boolean).join(' ')}
      </p>
      {props.reapply && rebuilds && (
        <Alert variant="error">
          {[
            t(
              'pages:onboarding.configure.diskRebuildsGcp',
              'A different boot disk size destroys this virtual machine and its disk, then builds a new one from the image. Everything on the disk is gone: the local database, the host credentials and the logs. GCP cannot resize this disk in place.',
            ),
            appliedDiskGb !== undefined && t('pages:onboarding.configure.diskKeepGcp', 'Leave empty to keep the current boot disk.'),
          ]
            .filter(Boolean)
            .join(' ')}
        </Alert>
      )}
    </>
  )
}

/**
 * Azure grows an OS disk in place, so a size change is a stop-resize-start
 * rather than a rebuild. It cannot shrink one, and the module default of 50 GB
 * is what an empty field asks for when no size is recorded to resend.
 */
export function AzureDiskHints(props: DiskHintProps) {
  const { t, diskSizeGb, appliedDiskGb } = props
  const bounds = diskSizeBounds('azure')
  const resizes = !!diskSizeGb && Number(diskSizeGb) !== appliedDiskGb
  const sentences = props.reapply
    ? appliedDiskGb !== undefined
      ? [
          t('pages:onboarding.configure.diskKeepAzure', 'Leave empty to keep the current boot disk.'),
          t('pages:onboarding.configure.diskCurrentAzure', { defaultValue: 'Current boot disk: {{size}} GB.', size: appliedDiskGb }),
        ]
      : [
          t('pages:onboarding.configure.diskUnrecordedAzure', 'No current boot disk size is on record for this machine. An empty field asks for the module default of 50 GB, and Azure cannot shrink an OS disk, so the apply fails if it runs on anything larger. Enter the size it runs on today.'),
        ]
    : [
        t('pages:onboarding.configure.diskRangeAzure', { defaultValue: '{{min}}–{{max}} GB. The Agion image is built at {{min}} GB.', ...bounds }),
      ]
  return (
    <>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {sentences.filter(Boolean).join(' ')}
      </p>
      {props.reapply && resizes && (
        <Alert variant="warning">
          {t(
            'pages:onboarding.configure.diskResizeStopsAzure',
            'A different boot disk size stops and deallocates this virtual machine while Azure resizes the disk, then starts it again. The disk grows in place and keeps everything on it; Azure cannot shrink an OS disk.',
          )}
        </Alert>
      )}
    </>
  )
}
